import { useState, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TranslationKeys } from 'app/translations';
import { useSnakeBar } from 'app/hooks';
import { useBulkLeftCompanyBackupMutation, useBulkLeftCompanyUpdateMutation } from 'app/hooks/api/admin';
import type { BulkLeftCompanyEmployee, BulkLeftCompanyBackupResponse } from '../../Services';
import type { ParseError } from './types';
import { parseCsv } from './parseCsv';
import { CSV_TEMPLATE_HEADERS } from './types';

export const useBulkLeftCompanyPage = () => {
  const intl = useIntl();
  const { showSuccessSnakeBar, showErrorSnakeBar } = useSnakeBar();

  const [activeStep, setActiveStep] = useState(0);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [parseErrors, setParseErrors] = useState<ParseError[]>([]);
  const [employees, setEmployees] = useState<BulkLeftCompanyEmployee[]>([]);
  const [backupData, setBackupData] = useState<BulkLeftCompanyBackupResponse | null>(null);
  const [backupError, setBackupError] = useState<string | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutateAsync: loadBackup, isLoading: isLoadingBackup } = useBulkLeftCompanyBackupMutation();
  const { mutateAsync: applyUpdate } = useBulkLeftCompanyUpdateMutation();

  // ── Template download ──────────────────────────────────────────────────────

  const handleDownloadTemplate = useCallback(() => {
    const blob = new Blob([CSV_TEMPLATE_HEADERS], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'bulk-left-company-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  }, []);

  // ── Step 0: CSV upload ─────────────────────────────────────────────────────

  const handleFileUpload = useCallback(async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    setCsvFile(file);
    setParseErrors([]);
    setEmployees([]);
    setBackupData(null);
    setBackupError(null);

    const result = await parseCsv(file);
    if (result.ok) {
      setEmployees(result.employees);
    } else {
      setParseErrors(result.errors);
    }
  }, []);

  const handleLoadPreview = useCallback(async () => {
    if (!employees.length) return;
    setBackupError(null);

    try {
      const response = await loadBackup(employees);
      setBackupData(response.data);
      setActiveStep(1);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ??
        err?.message ??
        intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_backupError });
      setBackupError(message);
    }
  }, [employees, loadBackup, intl]);

  // ── Step 1: Review ─────────────────────────────────────────────────────────

  const handleOpenConfirm = useCallback(() => {
    setIsConfirmOpen(true);
  }, []);

  // ── Step 2: Confirm & apply ────────────────────────────────────────────────

  const handleConfirm = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await applyUpdate(employees);
      showSuccessSnakeBar({
        message: intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_submitSuccess }),
      });
      setActiveStep(2);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ??
        err?.message ??
        intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_submitError });
      showErrorSnakeBar({ message });
    } finally {
      setIsSubmitting(false);
    }
  }, [applyUpdate, employees, intl, showSuccessSnakeBar, showErrorSnakeBar]);

  const handleReset = useCallback(() => {
    setActiveStep(0);
    setCsvFile(null);
    setParseErrors([]);
    setEmployees([]);
    setBackupData(null);
    setBackupError(null);
  }, []);

  // ── Derived ────────────────────────────────────────────────────────────────

  const canLoadPreview = useMemo(
    () => employees.length > 0 && parseErrors.length === 0 && !isLoadingBackup,
    [employees.length, parseErrors.length, isLoadingBackup],
  );

  return {
    activeStep,
    csvFile,
    parseErrors,
    employees,
    backupData,
    backupError,
    isConfirmOpen,
    setIsConfirmOpen,
    isSubmitting,
    isLoadingBackup,
    canLoadPreview,
    handleDownloadTemplate,
    handleFileUpload,
    handleLoadPreview,
    handleOpenConfirm,
    handleConfirm,
    handleReset,
  };
};
